import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt } from '@fortawesome/free-solid-svg-icons';

// Alias de la cuenta de Mercado Pago, que el home les muestra a todos los usuarios
// (ver AliasCuenta). Lo cambia solo un admin de la cuenta; sin onGuardar (operadores,
// o el administrador general viendo como otro) se muestra sin poder editarlo.
// Si la cuenta tiene CVU el alias llega solo (aliasAutomatico.js en functions) y sale en
// el home; el switch "Mostrar alias en el panel" (onMostrar) lo saca para toda la cuenta.
function CampoAlias({ alias, mostrar, onGuardar, onMostrar }) {
    const [valor, setValor] = useState(alias || '');
    const [guardando, setGuardando] = useState(false);
    const [mensaje, setMensaje] = useState(null);
    const [cambiandoMostrar, setCambiandoMostrar] = useState(false);

    useEffect(() => {
        setValor(alias || '');
    }, [alias]);

    if (!onGuardar) {
        if (!alias) return null;
        return (
            <div className="profile-field">
                <label>Alias de Mercado Pago</label>
                <div className="profile-value">{alias}</div>
            </div>
        );
    }

    const nuevo = valor.trim().toLowerCase();
    const cambio = nuevo !== (alias || '');

    const guardar = async (e) => {
        e.preventDefault();
        setGuardando(true);
        setMensaje(null);
        try {
            await onGuardar(nuevo || null);
            setMensaje({
                tipo: 'ok',
                texto: !nuevo ? 'Alias quitado' : mostrar ? 'Alias guardado: ya lo ven todos en el panel' : 'Alias guardado'
            });
        } catch (error) {
            setMensaje({ tipo: 'error', texto: error.message });
        } finally {
            setGuardando(false);
        }
    };

    const cambiarMostrar = async (e) => {
        const valorNuevo = e.target.checked;
        setCambiandoMostrar(true);
        setMensaje(null);
        try {
            await onMostrar(valorNuevo);
            setMensaje({ tipo: 'ok', texto: valorNuevo ? 'Listo: ya lo ven todos en el panel' : 'Listo: ya no se muestra en el panel' });
        } catch (error) {
            setMensaje({ tipo: 'error', texto: error.message });
        } finally {
            setCambiandoMostrar(false);
        }
    };

    const ayuda = !alias
        ? 'Lo ven todos los usuarios de la cuenta arriba de las transferencias.'
        : mostrar
            ? 'Lo ven todos los usuarios de la cuenta arriba de las transferencias.'
            : 'No se muestra en el panel.';

    return (
        <form className="profile-field" onSubmit={guardar}>
            <label htmlFor="perfil-alias">Alias de Mercado Pago</label>
            <div className="alias-perfil">
                <input
                    id="perfil-alias"
                    type="text"
                    className="alias-perfil__input"
                    placeholder="ej: nombre.apellido"
                    value={valor}
                    onChange={(e) => {
                        setValor(e.target.value.replace(/[^a-zA-Z0-9.-]/g, '').slice(0, 20));
                        setMensaje(null);
                    }}
                    disabled={guardando}
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                />
                <button type="submit" className="alias-perfil__guardar" disabled={guardando || !cambio}>
                    {guardando ? 'Guardando…' : 'Guardar'}
                </button>
            </div>
            {onMostrar && alias && (
                <label className="alias-perfil__mostrar">
                    <span>Mostrar alias en el panel</span>
                    <input
                        type="checkbox"
                        role="switch"
                        className="alias-perfil__switch"
                        checked={!!mostrar}
                        onChange={cambiarMostrar}
                        disabled={cambiandoMostrar || guardando}
                    />
                </label>
            )}
            <div className={`alias-perfil__ayuda ${mensaje ? `alias-perfil__ayuda--${mensaje.tipo}` : ''}`}>
                {mensaje ? mensaje.texto : ayuda}
            </div>
        </form>
    );
}

function ProfileModal({ isOpen, onClose, currentUser, onLogout, alias, mostrarAlias = true, onGuardarAlias, onMostrarAlias }) {
    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
        }}>
            <div className="modal-content">
                <div className="modal-header">
                    <h2>Mi Perfil</h2>
                    <button className="close-button" onClick={onClose}>×</button>
                </div>

                <div className="profile-data">
                    <div className="profile-field">
                        <label>Nombre de Empresa</label>
                        <div className="profile-value">{currentUser?.businessName || 'No especificado'}</div>
                    </div>

                    <div className="profile-field">
                        <label>Email</label>
                        <div className="profile-value">{currentUser?.email || 'No especificado'}</div>
                    </div>

                    <CampoAlias alias={alias} mostrar={mostrarAlias} onGuardar={onGuardarAlias} onMostrar={onMostrarAlias} />

                    <button className="logout-button" onClick={onLogout}>
                        <FontAwesomeIcon icon={faSignOutAlt} className="logout-icon" />
                        Cerrar Sesión
                    </button>
                </div>

                <style jsx>{`
                    .modal-overlay {
                        position: fixed;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: rgba(0, 0, 0, 0.6);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 1000;
                    }

                    .modal-content {
                        background-color: white;
                        border-radius: 8px;
                        padding: 20px;
                        width: 320px;
                        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                        position: relative;
                    }

                    .modal-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 15px;
                        padding-bottom: 10px;
                        border-bottom: 1px solid #eee;
                    }

                    .close-button {
                        background: transparent;
                        border: none;
                        font-size: 24px;
                        cursor: pointer;
                        color: #666;
                    }

                    .profile-data {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }

                    .profile-field {
                        padding: 8px 0;
                    }

                    .profile-field label {
                        font-size: 0.9rem;
                        color: #666;
                        margin-bottom: 4px;
                        display: block;
                    }

                    .profile-value {
                        font-size: 1rem;
                        color: #333;
                    }

.alias-perfil {
                        display: flex;
                        gap: 8px;
                    }

                    .alias-perfil__input {
                        flex: 1;
                        min-width: 0;
                        padding: 10px 12px;
                        border: 1px solid #ced4da;
                        border-radius: 6px;
                        font-size: 1rem;
                        text-align: center;
                    }

                    .alias-perfil__input:focus {
                        outline: none;
                        border-color: #001c4d;
                    }

                    .alias-perfil__guardar {
                        padding: 10px 16px;
                        background-color: #001c4d;
                        color: white;
                        border: none;
                        border-radius: 6px;
                        font-size: 1rem;
                        cursor: pointer;
                    }

                    .alias-perfil__guardar:disabled {
                        opacity: 0.45;
                        cursor: default;
                    }

                    .profile-field .alias-perfil__mostrar {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 12px;
                        margin: 14px 0 0;
                        font-size: 0.95rem;
                        color: #333;
                        cursor: pointer;
                    }

                    .alias-perfil__switch {
                        appearance: none;
                        -webkit-appearance: none;
                        position: relative;
                        flex-shrink: 0;
                        width: 44px;
                        height: 24px;
                        margin: 0;
                        border-radius: 12px;
                        background-color: #ced4da;
                        cursor: pointer;
                        transition: background-color 0.2s;
                    }

                    .alias-perfil__switch::before {
                        content: '';
                        position: absolute;
                        top: 3px;
                        left: 3px;
                        width: 18px;
                        height: 18px;
                        border-radius: 50%;
                        background-color: white;
                        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
                        transition: transform 0.2s;
                    }

                    .alias-perfil__switch:checked {
                        background-color: #001c4d;
                    }

                    .alias-perfil__switch:checked::before {
                        transform: translateX(20px);
                    }

                    .alias-perfil__switch:focus-visible {
                        outline: 2px solid #001c4d;
                        outline-offset: 2px;
                    }

                    .alias-perfil__switch:disabled {
                        opacity: 0.5;
                        cursor: default;
                    }

                    .alias-perfil__ayuda {
                        margin-top: 6px;
                        font-size: 0.8rem;
                        color: #666;
                    }

                    .alias-perfil__ayuda--ok {
                        color: #1e7e34;
                    }

                    .alias-perfil__ayuda--error {
                        color: #b91c1c;
                    }

                    .logout-button {
                        margin-top: 20px;
                        padding: 12px;
                        background-color: #E23B3B;
                        color: white;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        width: 100%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        gap: 8px;
                        font-size: 1rem;
                        transition: background-color 0.2s;
                    }

                    .logout-button:hover {
                        background-color: #d62828;
                    }

                    :global(.logout-icon) {
                        font-size: 1.1rem;
                    }
                `}</style>
            </div>
        </div>
    );
}

export default ProfileModal; 